import React from 'react';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { AUSTRIAN_STATES, getDistrictsForState, getGemeindenForDistrict } from '../../data/austrianLocations';

/**
 * Cascading Bundesland → Bezirk → Gemeinde dropdowns.
 *
 * Props:
 *   value: { state, district, gemeinde }
 *   onChange(next): receives updated location triplet
 *   testIdPrefix: optional, defaults to "job"
 *   gemeindeOptional: whether to show "— keine —" placeholder option (default true)
 */
export const LocationSelector = ({
  value,
  onChange,
  testIdPrefix = 'job',
  gemeindeOptional = true,
  required = true,
}) => {
  const { state = '', district = '', gemeinde = '' } = value || {};

  const handleStateChange = (next) =>
    onChange({ state: next, district: '', gemeinde: '' });

  const handleDistrictChange = (next) =>
    onChange({ state, district: next, gemeinde: '' });

  const handleGemeindeChange = (next) =>
    onChange({ state, district, gemeinde: next === '__none__' ? '' : next });

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <div className="space-y-2">
        <Label>Bundesland {required && '*'}</Label>
        <Select value={state || undefined} onValueChange={handleStateChange}>
          <SelectTrigger data-testid={`${testIdPrefix}-state-select`}>
            <SelectValue placeholder="Bundesland wählen" />
          </SelectTrigger>
          <SelectContent>
            {AUSTRIAN_STATES.map((s) => (
              <SelectItem key={s} value={s}>{s}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label>Bezirk {required && '*'}</Label>
        <Select
          value={district || undefined}
          onValueChange={handleDistrictChange}
          disabled={!state}
        >
          <SelectTrigger data-testid={`${testIdPrefix}-district-select`}>
            <SelectValue placeholder="Bezirk wählen" />
          </SelectTrigger>
          <SelectContent className="max-h-64">
            {getDistrictsForState(state).map((d) => (
              <SelectItem key={d} value={d}>{d}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label>Gemeinde{gemeindeOptional ? '' : ' *'}</Label>
        <Select
          value={gemeinde || undefined}
          onValueChange={handleGemeindeChange}
          disabled={!district}
        >
          <SelectTrigger data-testid={`${testIdPrefix}-gemeinde-select`}>
            <SelectValue placeholder={gemeindeOptional ? 'Gemeinde (optional)' : 'Gemeinde wählen'} />
          </SelectTrigger>
          <SelectContent className="max-h-64">
            {gemeindeOptional && <SelectItem value="__none__">— keine —</SelectItem>}
            {getGemeindenForDistrict(district).map((g) => (
              <SelectItem key={g.name} value={g.name}>{g.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};
